import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { useCallback, useMemo, useState } from 'react'

export interface BrandAvailability {
  hasContent: boolean
  isLoading: boolean
}

interface UseEligibleBrandSelectionResult {
  orderedCampaigns: CrossBrandMallHomeCampaign[]
  eligibleCampaigns: CrossBrandMallHomeCampaign[]
  selectedCampaign: CrossBrandMallHomeCampaign | null
  isAvailabilityResolved: boolean
  setAvailability: (campaignId: number, availability: BrandAvailability) => void
  setSelectedCampaignId: (campaignId: number) => void
}

/**
 * 從一段 brand section 的所有 campaigns 中，挑出「可顯示」的品牌並維護當前選擇。
 */
export const useEligibleBrandSelection = (campaigns: CrossBrandMallHomeCampaign[]): UseEligibleBrandSelectionResult => {
  const [availabilities, setAvailabilities] = useState<Record<number, BrandAvailability>>({})
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)

  const setAvailability = useCallback((campaignId: number, availability: BrandAvailability) => {
    setAvailabilities((prev) => {
      const existing = prev[campaignId]
      if (existing && existing.hasContent === availability.hasContent && existing.isLoading === availability.isLoading) {
        return prev
      }
      return { ...prev, [campaignId]: availability }
    })
  }, [])

  const orderedCampaigns = useMemo(() => [...campaigns].sort((a, b) => a.display_order - b.display_order), [campaigns])

  const isAvailabilityResolved = orderedCampaigns.length > 0
    && orderedCampaigns.every((campaign) => {
      const availability = availabilities[campaign.campaign_id]
      return availability !== undefined && !availability.isLoading
    })

  const eligibleCampaigns = useMemo(
    () => orderedCampaigns.filter(campaign => availabilities[campaign.campaign_id]?.hasContent),
    [orderedCampaigns, availabilities],
  )

  const selectedCampaign = useMemo(() => {
    if (!isAvailabilityResolved) return null
    return eligibleCampaigns.find(campaign => campaign.campaign_id === selectedCampaignId) ?? eligibleCampaigns[0] ?? null
  }, [isAvailabilityResolved, eligibleCampaigns, selectedCampaignId])

  return {
    orderedCampaigns,
    eligibleCampaigns,
    selectedCampaign,
    isAvailabilityResolved,
    setAvailability,
    setSelectedCampaignId,
  }
}
